/**
 * Universal Chakra Data Service
 *
 * Provides lazy loading and caching for all chakra types:
 * - Muhurta Chakra
 * - Navamsa Chakra
 * - Ashtaka Varga
 * - Dasha patterns
 * - etc. (10+ total)
 *
 * Principles:
 * - Lazy loading: data fetched only when first needed
 * - Caching: subsequent requests use cached data
 * - Chart-specific: each chart has its own data cache
 * - Type-safe: full TypeScript support
 */

import { ReadyChartData } from '../types/ReadyChartData';
import { devWarn, devError } from '../utils/devLog';
import { coordinateToDecimal, parseDMSToDecimal } from '../utils/astrology/coordinates';
import {
    DivisionalChakraType,
    ChakraStartPoint,
    getDivisionalChakraConfig
} from '../config/divisionalChakraConfig';
import { chartColorPresetManager } from '../config/chartColorPresets';
import { calculateCharaKarakas } from '../components/common/AstrologyChart/DataSections/data/CharaKarakaTableData';
import { NAKSHATRA_PADA_CONFIG } from '../config/nakshatraDefaults';
import { getAshtamshaGrahaRuler, getAshtamshaPlanetCode } from '../config/ashtamshaRulers';
import { getHoraRulers, HORA_RING_CONFIG, HORA_RING_CONFIG_24, DEFAULT_HORA_CLOCK_DESIGN, HoraClockDesignConfig } from '../config/horaClockConfig';
import type { CircularRingData, CircularRingSector } from '../components/common/AstrologyChart/universal_architecture/modules/CircularRingRenderer';
import type { RashiChakraData, RashiChakraPlanet, RashiChakraChart } from '../components/common/AstrologyChart/universal_architecture/modules/RashiChakraRenderer';
import { getRashiDisplayName, getPlanetDisplayName, getElementType } from '../components/common/AstrologyChart/universal_architecture/config/PlanetLayerConfig';
import { getElementColor } from '../config/chartRenderingColors';
import { FONTS } from '../config/fonts';
import { DisplayModeService } from '../components/common/AstrologyChart/services/DisplayModeService';
import { getHouseNumberFromRasiString } from '../components/common/AstrologyChart/universal_architecture/utils/HouseMappingUtils';

export type ChakraType =
    | 'muhurta'
    | 'ashtamsha'
    | 'nakshatra'
    | 'navamsa'
    | 'ashtaka_varga'
    | 'dasha_periods'
    | 'hora'
    | 'drekkana'
    | 'chaturthamsa'
    | 'saptamsa'
    | 'd7'
    | 'd8'
    | 'dasamsa'
    | 'dvadasamsa'
    | 'd12'
    | 'shodasamsa'
    | 'vimsamsa'
    | 'd20'
    | 'chaturvimsamsa'
    | 'd24'
    | 'saptavimsamsa'
    | 'trimsamsa'
    | 'd30'
    | 'khavedamsa'
    | 'd40'
    | 'akshavedamsa'
    | 'shashtiamsa'
    | 'd60';

export interface ChakraData {
    chartId: string;
    chakraType: ChakraType;
    data: any; // Specific to each chakra type
    metadata: {
        lastUpdated: Date;
        cacheExpiry?: Date;
        source: 'api' | 'cache' | 'calculation';
    };
}

export interface MuhurtaChakraData {
    divisions: number; // e.g., 30
    sectors: Array<{
        index: number; // 1-30
        startDegree: number;
        endDegree: number;
        ruler: string;
        rulerCode: string;
    }>;
    sunriseLongitude: string; // Decimal format (e.g., "214.697278")
    chakraLongitude: string; // Decimal format for ML marker
    varaNumber: number; // 1-7 for day of week
}

export interface AshtamshaChakraData {
    divisions: number; // 8 for D8
    sectors: Array<{
        index: number; // 1-8
        startDegree: number;
        endDegree: number;
        ruler: string;
        rulerCode: string;
    }>;
    sunriseLongitude: string; // Decimal format (e.g., "214.697278")
    chakraLongitude: string; // Decimal format for positioning
    varaNumber: number; // 0-6 for day of week (API format)
}

export interface NakshatraChakraData {
    divisions: number; // 27 for Nakshatra
    sectors: Array<{
        index: number; // 1-27
        startDegree: number;
        endDegree: number;
        nakshatraName: string;
    }>;
    sunriseLongitude: string; // Decimal format (e.g., "214.697278")
    // No chakraLongitude needed as per requirements (no ML markers)
}

/**
 * Universal divisional chakra data (D7, D8, D12, D20, D24, D30, D40, D60)
 */
export interface UniversalDivisionalChakraData {
    divisions: number; // Number of sectors
    sectors: Array<{
        index: number; // 1-N
        startDegree: number;
        endDegree: number;
        ruler?: string; // Optional ruler name
        rulerCode?: string; // Optional ruler code
    }>;
    startPointLongitude: string; // Decimal format (e.g., "214.697278")
    startPointType: ChakraStartPoint; // What point this chakra starts from
    chakraType: DivisionalChakraType; // d7, d8, d12, etc.
    rulers?: string[]; // Optional rulers array
}

export interface ChakraRequest {
    chartId: string;
    chakraType: ChakraType;
    forceRefresh?: boolean;
    chartData?: ReadyChartData; // Optional for calculations
    startPoint?: ChakraStartPoint; // For divisional chakras (d7, d8, d12, etc.)
    rulers?: string[]; // Optional rulers array for divisional chakras
    customSettings?: { degrees: number; label: string }; // For custom start point
}

export interface ChakraResponse<T = any> {
    success: boolean;
    data?: T;
    error?: string;
    cached: boolean;
    source: 'api' | 'cache' | 'calculation';
}

/**
 * Universal service for all chakra data management
 */
export class ChakraDataService {
    private static instance: ChakraDataService;
    private cache: Map<string, ChakraData> = new Map();
    private loadingPromises: Map<string, Promise<ChakraResponse>> = new Map();

    private constructor() {}

    public static getInstance(): ChakraDataService {
        if (!ChakraDataService.instance) {
            ChakraDataService.instance = new ChakraDataService();
        }
        return ChakraDataService.instance;
    }

    /**
     * Get chakra data with lazy loading and caching
     */
    public async getChakraData<T = any>(request: ChakraRequest): Promise<ChakraResponse<T>> {
        const cacheKey = this.generateCacheKey(request.chartId, request.chakraType, request.startPoint, request.customSettings);

        // Check if already loading to prevent duplicate requests
        if (this.loadingPromises.has(cacheKey)) {
            return await this.loadingPromises.get(cacheKey)!;
        }

        // Check cache first (unless force refresh)
        if (!request.forceRefresh) {
            const cachedData = this.getFromCache<T>(cacheKey);
            if (cachedData) {
                return {
                    success: true,
                    data: cachedData,
                    cached: true,
                    source: 'cache'
                };
            }
        }

        // Start loading
        const loadingPromise = this.loadChakraData<T>(request);
        this.loadingPromises.set(cacheKey, loadingPromise);

        try {
            const result = await loadingPromise;
            return result;
        } finally {
            // Clean up loading promise
            this.loadingPromises.delete(cacheKey);
        }
    }

    /**
     * Internal method to load chakra data from appropriate source
     */
    private async loadChakraData<T>(request: ChakraRequest): Promise<ChakraResponse<T>> {
        const cacheKey = this.generateCacheKey(request.chartId, request.chakraType, request.startPoint, request.customSettings);


        try {
            let data: T;
            let source: 'api' | 'calculation' = 'api';

            // Route to specific loader based on chakra type
            switch (request.chakraType) {
                // Universal divisional chakras (MIGRATED: muhurta and nakshatra use universal handler)
                case 'muhurta':
                case 'nakshatra':
                case 'd7':
                case 'd8':
                case 'd12':
                case 'd20':
                case 'd24':
                case 'd30':
                case 'd40':
                case 'd60':
                    data = await this.loadUniversalDivisionalChakraData(request) as T;
                    source = 'calculation'; // All divisional chakras calculated locally
                    break;

                // Add more chakra types as needed
                default:
                    throw new Error(`Unsupported chakra type: ${request.chakraType}`);
            }

            // Cache the result
            this.storeInCache(cacheKey, {
                chartId: request.chartId,
                chakraType: request.chakraType,
                data,
                metadata: {
                    lastUpdated: new Date(),
                    cacheExpiry: this.calculateCacheExpiry(request.chakraType),
                    source
                }
            });


            return {
                success: true,
                data,
                cached: false,
                source
            };

        } catch (error) {
            devError(`❌ ChakraDataService: Failed to load ${request.chakraType} for chart ${request.chartId}:`, error);

            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
                cached: false,
                source: 'api'
            };
        }
    }

    /**
     * Load universal divisional chakra data (D7, D8, D12, D20, D24, D30, D40, D60)
     * Universal method that handles all divisional chakras with configurable start points
     */
    private async loadUniversalDivisionalChakraData(request: ChakraRequest): Promise<UniversalDivisionalChakraData> {
        const chakraType = request.chakraType as DivisionalChakraType;

        if (!request.chartData) {
            throw new Error(`Chart data required for ${chakraType} chakra calculation`);
        }

        const chartData = request.chartData;
        const config = getDivisionalChakraConfig(chakraType);

        // Auto-generate rulers for D7 and D8 if not provided
        let rulers = request.rulers;
        if (chakraType === 'd7' && !rulers) {
            rulers = this.getD7CharaKarakaRulers(request.chartId);
        } else if (chakraType === 'd8' && !rulers) {
            rulers = this.getD8AshtamshaRulers();
        }

        // Get start point
        const startPointType = request.startPoint || config.defaultStartPoint;
        let startPointLongitude: string;

        // Helper: Convert sunrise longitude (number or string) to string
        const getSunriseLongitudeString = (): string => {
            const value: any = chartData.astronomical?.sunrise_surya_longitude;
            if (typeof value === 'number') {
                return value.toString();
            }
            if (typeof value === 'string') {
                return value;
            }
            return '0';
        };

        // Extract start point longitude based on type

        switch (startPointType) {
            case 'default':
                // Fixed start point at 0° (used for Nakshatra - always starts at 0° Ashwini)
                startPointLongitude = '0° 0\' 0"';
                break;

            case 'sunrise':
                // API returns decimal number, convert to string
                startPointLongitude = getSunriseLongitudeString();
                break;

            case 'lagna':
                // Lagna is in planets array with code 'Lg'
                const lagnaFromPlanets = chartData.planets?.find((p: any) => p.graha === 'Lg');
                if (lagnaFromPlanets?.positionString) {
                    const parts = lagnaFromPlanets.positionString.split('|');
                    startPointLongitude = parts[2] || '0° 0\' 0"';
                } else if (chartData.lagna?.positionString) {
                    const parts = chartData.lagna.positionString.split('|');
                    startPointLongitude = parts[2] || '0° 0\' 0"';
                } else {
                    startPointLongitude = getSunriseLongitudeString();
                }
                break;

            case 'surya':
            case 'chandra':
            case 'mangala':
            case 'budha':
            case 'guru':
            case 'shukra':
            case 'shani':
            case 'rahu':
            case 'ketu':
                // Planets stored as SHORT CODES (Sy, Ch, Ma, etc.)
                const planetShortCodes: Record<string, string> = {
                    'surya': 'Sy', 'chandra': 'Ch', 'mangala': 'Ma', 'budha': 'Bu',
                    'guru': 'Gu', 'shukra': 'Sk', 'shani': 'Sa', 'rahu': 'Ra', 'ketu': 'Ke'
                };
                const shortCode = planetShortCodes[startPointType];
                const planet = chartData.planets?.find((p: any) => p.graha === shortCode);
                if (planet?.positionString) {
                    const parts = planet.positionString.split('|');
                    startPointLongitude = parts[2] || '0° 0\' 0"';
                } else {
                    startPointLongitude = getSunriseLongitudeString();
                }
                break;

            case 'custom':
                // Custom degree - directly use the provided degrees
                const customDegrees = request.customSettings?.degrees ?? 0;
                // Convert decimal degrees to DMS format for consistency
                const deg = Math.floor(customDegrees);
                const minFloat = (customDegrees - deg) * 60;
                const min = Math.floor(minFloat);
                const sec = Math.round((minFloat - min) * 60);
                startPointLongitude = `${deg}° ${min}' ${sec}"`;
                break;

            default:
                startPointLongitude = getSunriseLongitudeString();
        }

        // Convert absolute longitude (if string is already decimal, parseFloat; if DMS, parse)
        let absoluteLongitude: number;
        if (typeof startPointLongitude === 'string' && /^[\d.]+$/.test(startPointLongitude)) {
            // Already decimal string like "217.678163"
            absoluteLongitude = parseFloat(startPointLongitude);
        } else {
            // DMS format or need parsing
            absoluteLongitude = parseDMSToDecimal(startPointLongitude);
        }

        // CRITICAL: All divisional chakras (nakshatra, muhurta, D7-D60) cover full zodiac (360°)
        // Use absolute coordinates directly - NO rashi-relative conversion needed
        // Rashi-relative logic would only make sense for charts subdividing a SINGLE rashi,
        // but all our divisional chakras subdivide the ENTIRE zodiac
        const sectors = this.generateUniversalDivisionalSectors(
            config.sectors,
            config.degreesPerSector,
            absoluteLongitude.toString(), // Use absolute position
            rulers,
            chakraType
        );


        return {
            divisions: config.sectors,
            sectors: sectors, // Use absolute coordinates for rendering
            startPointLongitude: absoluteLongitude.toString(), // CRITICAL: Always pass decimal format!
            startPointType,
            chakraType,
            rulers: rulers
        };
    }

    /**
     * Generate universal divisional sectors
     */
    private generateUniversalDivisionalSectors(
        numberOfSectors: number,
        degreesPerSector: number,
        startPointLongitude: string,
        rulers?: string[],
        chakraType?: DivisionalChakraType
    ): UniversalDivisionalChakraData['sectors'] {
        const sectors: UniversalDivisionalChakraData['sectors'] = [];

        // Parse start point offset (already decimal string like "210.77964")
        const startPointOffset = parseFloat(startPointLongitude);


        // Nakshatra names for nakshatra chakra (27 sectors)
        const NAKSHATRA_NAMES = [
            'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu',
            'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta',
            'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha',
            'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada',
            'Uttara Bhadrapada', 'Revati'
        ];

        for (let i = 0; i < numberOfSectors; i++) {
            const startDegree = (i * degreesPerSector + startPointOffset) % 360;
            const endDegree = ((i + 1) * degreesPerSector + startPointOffset) % 360;

            const sector: UniversalDivisionalChakraData['sectors'][0] = {
                index: i + 1,
                startDegree,
                endDegree
            };

            // Add ruler if provided
            if (rulers && rulers[i]) {
                sector.ruler = rulers[i];
                sector.rulerCode = rulers[i];
            }

            // Add nakshatra names if this is nakshatra chakra
            if (chakraType === 'nakshatra' && numberOfSectors === 27) {
                sector.ruler = NAKSHATRA_NAMES[i];
                sector.rulerCode = NAKSHATRA_NAMES[i];
            }

            sectors.push(sector);
        }

        return sectors;
    }

    /**
     * Get D7 Chara Karaka rulers in format: ['Atma, Sy', 'Amatya, Ma', ...]
     * @param chartId - Chart ID to get data from context
     * @returns Array of 7 ruler strings
     */
    private getD7CharaKarakaRulers(chartId: string): string[] {
        const multipleChartsContext = window.multipleChartsContext;

        if (!multipleChartsContext?.charts.has(chartId)) {
            return [];
        }

        const chartInstance = multipleChartsContext.charts.get(chartId);

        if (!chartInstance?.apiResponse) {
            return [];
        }

        // Calculate Chara Karakas using existing logic
        const charaKarakaData = calculateCharaKarakas(chartInstance.apiResponse);

        // Format as: "KarakaName, PlanetCode"
        const rulers = charaKarakaData.charaKarakas.map(karaka => {
            const karakaShortName = karaka.karakaName.split(' ')[0];
            const planetCode = karaka.planet.short_name;
            return `${karakaShortName}, ${planetCode}`;
        });

        return rulers;
    }

    /**
     * Get D8 Ashtamsha Vara rulers in format: ['Indra', 'Ishana', 'Kubera', ...]
     * Based on ASHTAMSHA_DIVISION_ATTRIBUTES from AshtamshaTableData
     * @returns Array of 8 ruler strings (directional deities)
     */
    private getD8AshtamshaRulers(): string[] {
        // Digpalas (directional deities) with symmetric dual rulers
        // Only Indra (East) and Varuna (West) rule alone
        // Others have dual rulership: 2+8, 3+7, 4+6
        return [
            'Indra',           // 1 - East (sole ruler)
            'Ishana + Agni',   // 2 - Northeast + Southeast
            'Kubera + Yama',   // 3 - North + South
            'Vayu + Nirriti',  // 4 - Northwest + Southwest
            'Varuna',          // 5 - West (sole ruler)
            'Nirriti + Vayu',  // 6 - Southwest + Northwest
            'Yama + Kubera',   // 7 - South + North
            'Agni + Ishana'    // 8 - Southeast + Northeast
        ];
    }

    /**
     * Helper methods
     */
    private generateCacheKey(chartId: string, chakraType: ChakraType, startPoint?: string, customSettings?: { degrees: number; label: string }): string {
        // Include startPoint and customSettings in cache key for divisional chakras
        if (startPoint === 'custom' && customSettings) {
            return `${chartId}_${chakraType}_custom_${customSettings.degrees}_${customSettings.label}`;
        }
        if (startPoint) {
            return `${chartId}_${chakraType}_${startPoint}`;
        }
        return `${chartId}_${chakraType}`;
    }

    private getFromCache<T>(cacheKey: string): T | null {
        const cached = this.cache.get(cacheKey);
        if (!cached) return null;

        // Check expiry
        if (cached.metadata.cacheExpiry && cached.metadata.cacheExpiry < new Date()) {
            this.cache.delete(cacheKey);
            return null;
        }

        return cached.data as T;
    }

    private storeInCache(cacheKey: string, data: ChakraData): void {
        this.cache.set(cacheKey, data);
    }

    private calculateCacheExpiry(chakraType: ChakraType): Date | undefined {
        // Different cache expiry for different types
        const expiry = new Date();

        switch (chakraType) {
            case 'muhurta':
                expiry.setHours(expiry.getHours() + 1); // 1 hour for muhurta
                break;
            case 'navamsa':
            case 'hora':
                expiry.setHours(expiry.getHours() + 24); // 24 hours for divisional charts
                break;
            default:
                expiry.setHours(expiry.getHours() + 12); // 12 hours default
        }

        return expiry;
    }

    private getChartDataFromContext(chartId: string): ReadyChartData | null {
        try {
            const multipleChartsContext = window.multipleChartsContext;
            if (multipleChartsContext?.charts.has(chartId)) {
                const chartInstance = multipleChartsContext.charts.get(chartId);
                if (!chartInstance) return null;

                const chartData = chartInstance.chartData as any;
                if (!chartData) return null;

                // Enrich chartData with astronomical data from apiResponse if needed
                if (!chartData.astronomical && chartInstance.apiResponse) {
                    chartData.astronomical = {
                        sunrise_surya_longitude: (chartInstance.apiResponse as any)?.astronomical?.sunrise_surya_longitude || '0° 0\' 0"',
                        sunrise: (chartInstance.apiResponse as any)?.astronomical?.sunrise || '00:00:00',
                        sunset: (chartInstance.apiResponse as any)?.astronomical?.sunset || '00:00:00',
                        ayanamsa: (chartInstance.apiResponse as any)?.astronomical?.ayanamsa || '0',
                        sidereal_time: (chartInstance.apiResponse as any)?.astronomical?.sidereal_time || '00:00:00',
                        julian_day: (chartInstance.apiResponse as any)?.astronomical?.julian_day || 0
                    };
                }

                return chartData as ReadyChartData;
            }
            return null;
        } catch (error) {
            devError('Failed to get chart data from context:', error);
            return null;
        }
    }


    /**
     * Generate subdivision render data (e.g., Pada divisions for Nakshatra)
     * Returns ready-to-render division lines and labels based on config
     */
    public generateSubdivisionData(
        chakraType: DivisionalChakraType,
        startPointLongitude: number,
        showLabels: boolean = true
    ): {
        divisionLines: Array<{
            degree: number;
            length: number;
            style?: any;
            metadata?: Record<string, string | number>;
        }>;
        innerLabels: Array<{
            content: string;
            degree: number;
            offset: number;
            style?: any;
            metadata?: Record<string, string | number>;
        }>;
    } {

        const config = getDivisionalChakraConfig(chakraType);

        if (!config.hasSubdivisions || !config.subdivisions) {
            return { divisionLines: [], innerLabels: [] };
        }

        const { count: subdivisionsPerSector, degreesPerSubdivision } = config.subdivisions;
        const totalSubdivisions = config.sectors * subdivisionsPerSector;


        // Get styles from centralized config (chakra-specific)
        let lineStyle: any;
        let labelStyle: any;
        let labelOffset: number;

        if (chakraType === 'nakshatra') {
            // Nakshatra Pada styles from nakshatraDefaults
            const padaConfig = NAKSHATRA_PADA_CONFIG;
            lineStyle = {
                stroke: padaConfig.padaDivisionLine.stroke,
                strokeWidth: padaConfig.padaDivisionLine.strokeWidth,
                strokeDasharray: padaConfig.padaDivisionLine.strokeDasharray,
                strokeOpacity: padaConfig.padaDivisionLine.strokeOpacity
            };
            labelStyle = {
                fontFamily: padaConfig.padaNumberStyle.fontFamily,
                fontSize: padaConfig.padaNumberStyle.fontSize,
                fontWeight: padaConfig.padaNumberStyle.fontWeight,
                fill: padaConfig.padaNumberStyle.fill,
                stroke: padaConfig.padaNumberStyle.stroke,
                strokeWidth: padaConfig.padaNumberStyle.strokeWidth,
                opacity: padaConfig.padaNumberStyle.opacity
            };
            labelOffset = padaConfig.padaNumberOffset;
        } else {
            // Generic subdivision styles (fallback for other chakras)
            lineStyle = {
                stroke: '#666666',
                strokeWidth: 0.5,
                strokeDasharray: '2,2',
                strokeOpacity: 0.5
            };
            labelStyle = {
                fontFamily: 'Arial',
                fontSize: 8,
                fontWeight: 'normal',
                fill: '#888888',
                stroke: '#ffffff',
                strokeWidth: 0.2,
                opacity: 0.7
            };
            labelOffset = -25;
        }

        const divisionLines: any[] = [];
        const innerLabels: any[] = [];

        // Generate division lines and labels for each subdivision
        for (let i = 0; i < totalSubdivisions; i++) {
            const subdivisionDegree = (i * degreesPerSubdivision + startPointLongitude) % 360;

            // Log first few subdivisions for debugging
            // if (i < 3) {
            // }

            // Only add division line if not at sector boundary (sector boundaries already rendered)
            const isAtSectorBoundary = i % subdivisionsPerSector === 0;
            if (!isAtSectorBoundary) {
                const lineLength = chakraType === 'nakshatra'
                    ? NAKSHATRA_PADA_CONFIG.padaDivisionLine.length
                    : 30;

                divisionLines.push({
                    degree: subdivisionDegree,
                    length: lineLength,
                    style: lineStyle,
                    metadata: {
                        subdivisionIndex: i + 1,
                        sectorIndex: Math.floor(i / subdivisionsPerSector) + 1,
                        subdivisionWithinSector: (i % subdivisionsPerSector) + 1
                    }
                });
            }

            // Add labels if requested (e.g., "1", "2", "3", "4" for Pada)
            if (showLabels) {
                const subdivisionWithinSector = (i % subdivisionsPerSector) + 1;
                const labelDegree = subdivisionDegree + (degreesPerSubdivision / 2); // Center of subdivision

                innerLabels.push({
                    content: subdivisionWithinSector.toString(),
                    degree: labelDegree % 360,
                    offset: labelOffset,
                    style: labelStyle,
                    metadata: {
                        subdivisionIndex: i + 1,
                        sectorIndex: Math.floor(i / subdivisionsPerSector) + 1,
                        subdivisionWithinSector
                    }
                });
            }
        }


        return { divisionLines, innerLabels };
    }

    /**
     * Clear cache for specific chart or chakra type
     */
    public clearCache(chartId?: string, chakraType?: ChakraType): void {
        if (!chartId && !chakraType) {
            // Clear all cache
            this.cache.clear();
            return;
        }

        if (chartId && chakraType) {
            // Clear specific cache entry
            const cacheKey = this.generateCacheKey(chartId, chakraType);
            this.cache.delete(cacheKey);
            return;
        }

        // Clear by chart ID or chakra type
        const keysToDelete: string[] = [];
        for (const [key, data] of this.cache.entries()) {
            if (chartId && data.chartId === chartId) {
                keysToDelete.push(key);
            } else if (chakraType && data.chakraType === chakraType) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
    }

    /**
     * Prepare universal Nakshatra Chakra data for UniversalChakraRenderer
     */
    public async prepareUniversalNakshatraData(
        chartId: string
    ): Promise<import('../types/UniversalChakraTypes').UniversalChakraRenderData> {
        // Get chart data from context
        const chartData = this.getChartDataFromContext(chartId);
        if (!chartData) {
            throw new Error(`Chart data not found for ${chartId}`);
        }

        // Load existing nakshatra data
        const response = await this.getChakraData<NakshatraChakraData>({
            chartId,
            chakraType: 'nakshatra',
            chartData // Pass chartData to the loader
        });

        if (!response.success || !response.data) {
            throw new Error('Failed to load Nakshatra chakra data');
        }

        const nakshatraData = response.data;

        // Convert to universal format
        const universalData: import('../types/UniversalChakraTypes').UniversalChakraRenderData = {
            chartId,
            chakraType: 'nakshatra',
            divisions: 27,
            startPointLongitude: nakshatraData.sunriseLongitude,
            sectors: nakshatraData.sectors.map(sector => ({
                index: sector.index,
                startDegree: sector.startDegree,
                endDegree: sector.endDegree,
                labels: [
                    {
                        position: 'center',
                        offset: 0, // On center line
                        content: sector.nakshatraName,
                        className: 'nakshatra-name',
                        style: {
                            fontSize: 9,
                            fontWeight: 'normal'
                        }
                    }
                ],
                metadata: {
                    nakshatraName: sector.nakshatraName
                }
            })),
            visual: {
                sectorFillColors: ['#FFD700', '#FFA500', '#FF8C00'], // Golden colors
                sectorStrokeWidth: 0.5,
                sectorFillOpacity: 0.1,
                sectorStrokeOpacity: 0.2
            }
        };

        return universalData;
    }

    /**
     * Prepare universal divisional chakra data (D7-D60) for UniversalChakraRenderer
     */
    public async prepareUniversalDivisionalData(
        chartId: string,
        chakraType: DivisionalChakraType,
        startPoint: ChakraStartPoint,
        rulers?: string[],
        customSettings?: { degrees: number; label: string }
    ): Promise<import('../types/UniversalChakraTypes').UniversalChakraRenderData> {
        // Get chart data from context
        const chartData = this.getChartDataFromContext(chartId);
        if (!chartData) {
            throw new Error(`Chart data not found for ${chartId}`);
        }

        // Load existing divisional data
        const response = await this.getChakraData<UniversalDivisionalChakraData>({
            chartId,
            chakraType,
            startPoint,
            rulers,
            customSettings,
            chartData // Pass chartData to the loader
        });

        if (!response.success || !response.data) {
            throw new Error(`Failed to load ${chakraType} chakra data`);
        }

        const divisionalData = response.data;

        // Get color palette from muhurtaDivisions (rainbow gradient - 30 colors)
        const colorPalette = chartColorPresetManager.getCurrentPreset().muhurtaDivisions.sectorFillColors;
        const divisions = divisionalData.divisions;

        // For Muhurta, get chakra_longitude for ML marker
        let muhurtaChakraLongitude: number | undefined;
        if (chakraType === 'muhurta') {
            const detailedPanchanga = chartData.detailed_panchanga;
            if (detailedPanchanga?.muhurta_details?.chakra_longitude) {
                muhurtaChakraLongitude = parseDMSToDecimal(detailedPanchanga.muhurta_details.chakra_longitude);
            }
        }

        // For D8 (Ashtamsha), get vara number for planetary rulers
        let d8VaraNumber: number | undefined;
        if (chakraType === 'd8') {
            const detailedPanchanga = chartData.detailed_panchanga;
            // API returns 0-based: 0=Sunday, 1=Monday, ..., 6=Saturday
            d8VaraNumber = detailedPanchanga?.vara_details?.number ?? 0;
        }

        // Convert to universal format
        const universalData: import('../types/UniversalChakraTypes').UniversalChakraRenderData = {
            chartId,
            chakraType,
            divisions: divisionalData.divisions,
            startPointLongitude: divisionalData.startPointLongitude,
            sectors: divisionalData.sectors.map(sector => {
                // Distribute 30 colors across sectors evenly
                const colorIndex = Math.floor((sector.index - 1) * 30 / divisions);
                const sectorColor = colorPalette[colorIndex];

                // For D8: combine planetary ruler (from vara) with digpala
                // Format: "Sy, Indra" or "Ch, Ishana + Agni"
                let displayRulerCode = sector.rulerCode;
                if (chakraType === 'd8' && d8VaraNumber !== undefined && sector.rulerCode) {
                    try {
                        const planetaryRuler = getAshtamshaGrahaRuler(sector.index, d8VaraNumber);
                        const planetCode = getAshtamshaPlanetCode(planetaryRuler);
                        displayRulerCode = `${planetCode}, ${sector.rulerCode}`;
                    } catch (error) {
                        // Fallback to digpala only if planetary calculation fails
                        devWarn(`⚠️ D8 planetary ruler calculation failed for sector ${sector.index}:`, error);
                    }
                }

                return {
                    index: sector.index,
                    startDegree: sector.startDegree,
                    endDegree: sector.endDegree,
                    // Always show sector numbers
                    labels: [
                        {
                            position: 'inner',
                            offset: -60,
                            content: sector.index.toString(),
                            className: `${chakraType}-sector-number`
                        },
                        // Show ruler code if available (curved text along arc)
                        ...(displayRulerCode ? [{
                            position: 'outer' as const,
                            offset: 125,
                            content: displayRulerCode,
                            className: `${chakraType}-ruler-code`,
                            curved: true,
                            curvedDistance: 115,
                            // For Nakshatra: enable two-line rendering for long names (>8 chars with space)
                            twoLine: chakraType === 'nakshatra' && displayRulerCode.length > 8 && displayRulerCode.includes(' ')
                        }] : [])
                    ],
                    // Per-sector color override
                    fillColor: sectorColor,
                    strokeColor: sectorColor,
                    metadata: (sector.ruler && sector.rulerCode) ? {
                        ruler: sector.ruler,
                        rulerCode: sector.rulerCode
                    } : undefined
                };
            }),
            visual: {
                sectorFillColors: colorPalette,
                sectorStrokeWidth: chartColorPresetManager.getCurrentPreset().muhurtaDivisions.sectorStrokeWidth,
                sectorFillOpacity: chartColorPresetManager.getCurrentPreset().muhurtaDivisions.sectorFillOpacity,
                sectorStrokeOpacity: chartColorPresetManager.getCurrentPreset().muhurtaDivisions.sectorStrokeOpacity
            },
            metadata: {
                startPointType: divisionalData.startPointType,
                rulers: divisionalData.rulers
            },
            // Position labels: custom start point OR Muhurta ML marker
            positionLabels: startPoint === 'custom' && customSettings ? [{
                name: customSettings.label,
                longitude: customSettings.degrees,
                styleType: 'square'
            }] : (chakraType === 'muhurta' && muhurtaChakraLongitude !== undefined) ? [{
                name: 'ML',
                longitude: muhurtaChakraLongitude,
                styleType: 'squareOrange'
            }] : undefined
        };

        return universalData;
    }

    // REMOVED: prepareUniversalAshtamshaData - Ashtamsha now uses prepareUniversalDivisionalData

    /**
     * Parse "HH:MM:SS" time string to seconds since midnight
     */
    private timeToSeconds(timeStr: string): number {
        const parts = timeStr.split(':').map(Number);
        return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    }

    /**
     * Prepare Hora Clock ring data for CircularRingRenderer
     *
     * Calculation: sunrise-to-sunrise / 24 equal parts
     * - Total duration = next_sunrise - sunrise (handles midnight crossing)
     * - Each hora = totalDuration / 24
     * - horaIndex 0-11 = day, 12-23 = night
     * - ML line proportional to hora progress within sector
     * - Minute hand = floor(horaProgress * 60)
     */
    public prepareHoraClockData(chartId: string, hourMode: 12 | 24 = 12): CircularRingData | null {
        const multipleChartsContext = window.multipleChartsContext;
        if (!multipleChartsContext?.charts.has(chartId)) return null;

        const chartInstance = multipleChartsContext.charts.get(chartId);
        if (!chartInstance?.apiResponse) return null;

        const apiResponse = chartInstance.apiResponse as any;
        const detailedPanchanga = apiResponse?.detailed_panchanga;
        if (!detailedPanchanga) return null;

        // Get vara lord directly from API (most reliable)
        const varaLord = detailedPanchanga.vara_details?.ruling_planet;
        if (!varaLord) return null;

        const astronomical = apiResponse?.astronomical;
        const sunriseStr = astronomical?.sunrise;   // "06:48:30"
        const basicInfo = apiResponse?.basic_info;
        const chartTimeStr = basicInfo?.time;       // "HH:MM:SS"
        if (!sunriseStr || !chartTimeStr) return null;

        // Design config from chart settings (or default)
        const designConfig: HoraClockDesignConfig =
            (chartInstance as any)?.settings?.horaClockDesign ?? DEFAULT_HORA_CLOCK_DESIGN;

        // --- Sunrise-to-sunrise / 24 equal parts calculation ---
        const sunriseSec = this.timeToSeconds(sunriseStr);

        // next_sunrise from API, or estimate sunrise + 24h
        const nextSunriseStr = astronomical?.next_sunrise;
        let nextSunriseSec: number;
        if (nextSunriseStr) {
            const raw = this.timeToSeconds(nextSunriseStr);
            // next_sunrise is next day — if raw <= sunriseSec, it crossed midnight
            nextSunriseSec = raw <= sunriseSec ? raw + 86400 : raw;
        } else {
            nextSunriseSec = sunriseSec + 86400; // fallback: exactly 24h
        }

        const totalDuration = nextSunriseSec - sunriseSec; // seconds from sunrise to next sunrise
        const horaDuration = totalDuration / 24;            // each hora duration

        // Elapsed seconds since sunrise (handle pre-sunrise: wrap around)
        const chartTimeSec = this.timeToSeconds(chartTimeStr);
        let elapsed = chartTimeSec - sunriseSec;
        if (elapsed < 0) elapsed += 86400;

        // Hora index (0-23) and progress within hora (0.0–1.0)
        const horaIndex = Math.min(Math.floor(elapsed / horaDuration), 23);
        const horaProgress = (elapsed - horaIndex * horaDuration) / horaDuration;

        const isNight = horaIndex >= 12;
        const displaySector = hourMode === 24 ? horaIndex : horaIndex % 12;

        // Display modes from global settings
        const rashiMode = (typeof window !== 'undefined' && (window as any).globalRashiDisplayMode) || 'full';
        const planetMode = DisplayModeService.getDisplayMode();

        // Build sectors — 12 or 24
        let sectors: CircularRingSector[];
        if (hourMode === 24) {
            // 24 sectors: all horas on one clock face
            const dayRulers = getHoraRulers(varaLord, 0);
            const nightRulers = getHoraRulers(varaLord, 12);
            const allRulers = [...dayRulers, ...nightRulers];
            sectors = allRulers.map((r, i) => {
                const containerIndex = i % 12 === 11 ? 0 : (i % 12) + 1;
                return {
                    index: i,
                    label: getPlanetDisplayName(r.code, planetMode),
                    sublabel: getRashiDisplayName(containerIndex, rashiMode),
                    topLabel: String(i + 1),
                    highlighted: i === horaIndex,
                };
            });
        } else {
            // 12 sectors: day or night half
            const hourOffset = isNight ? 12 : 0;
            const rulers = getHoraRulers(varaLord, hourOffset);
            sectors = rulers.map((r, i) => {
                const containerIndex = i === 11 ? 0 : i + 1;
                return {
                    index: i,
                    label: getPlanetDisplayName(r.code, planetMode),
                    sublabel: getRashiDisplayName(containerIndex, rashiMode),
                    topLabel: String(i + 1),
                    highlighted: i === (horaIndex % 12),
                };
            });
        }

        // Hour hand — elapsed time mapped to clock face (0° = sunrise)
        const elapsedHours = elapsed / 3600;
        const mlAngleDeg = hourMode === 24
            ? (elapsedHours / 24) * 360
            : (elapsedHours % 12) / 12 * 360;

        // Minute hand — elapsed minutes within the current hour (0-59 → 0°-360°)
        const currentMinute = (elapsed % 3600) / 60;

        // Elapsed since sunrise formatted as "Xh Ym Zs"
        const elH = Math.floor(elapsed / 3600);
        const elM = Math.floor((elapsed % 3600) / 60);
        const elS = Math.floor(elapsed % 60);
        const elapsedLabel = `${elH}h ${elM}m ${elS}s`;

        // Tattva icons: 5 per sector (one per minute tick), 60 total around the ring
        // Each hora sector is divided into 5 equal tattva periods
        // Chakra order: Muladhara → Vishuddha
        const TATTVA_CYCLE = [
            '/tattvas/prithvi.png',
            '/tattvas/jala.png',
            '/tattvas/agni.png',
            '/tattvas/vayu.png',
            '/tattvas/akasha.png',
        ];
        const tattvaIcons = Array.from({ length: 60 }, (_, i) => ({
            angleDeg: i * 6, // 360/60 = 6° per minute
            iconPath: TATTVA_CYCLE[i % TATTVA_CYCLE.length],
        }));

        return {
            ringType: 'hora',
            chartId,
            sectors,
            labelFont: planetMode === 'symbols' ? FONTS.chart.symbols : undefined,
            sublabelFont: rashiMode === 'symbols' ? FONTS.chart.symbols : undefined,
            radialLines: [{
                name: 'ML',
                angleDeg: mlAngleDeg,
                color: designConfig.mlLineColor,
                strokeWidth: designConfig.mlLineWidth,
                opacity: designConfig.mlLineOpacity,
                label: 'H',
            }],
            minuteTicks: {
                count: 60,
                currentMinute,
            },
            config: hourMode === 24 ? HORA_RING_CONFIG_24 : HORA_RING_CONFIG,
            highlightedSectorIndex: displaySector,
            hourMode,
            designConfig,
            infoLabels: [{
                title: 'Elapsed Since Sunrise',
                text: elapsedLabel,
                position: 'top-right',
            }],
            tattvaIcons,
        };
    }

    // ─────────────────────────────────────────────────────────────────────
    // Rashi Chakra (South Indian style divisional charts)
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Prepare data for South Indian style Rashi Chakra view
     * @param chartId - chart ID
     * @param enabledCharts - which divisional charts to render (e.g. ['D1', 'D9'])
     */
    public prepareRashiChakraData(
        chartId: string,
        enabledCharts: string[] = ['D1', 'D9']
    ): RashiChakraData | null {
        const multipleChartsContext = window.multipleChartsContext;
        if (!multipleChartsContext?.charts.has(chartId)) return null;

        const chartInstance = multipleChartsContext.charts.get(chartId);
        if (!chartInstance?.apiResponse) return null;

        const apiResponse = chartInstance.apiResponse as any;
        const rawPlanets = apiResponse?.graha_positions?.planets;
        if (!Array.isArray(rawPlanets) || rawPlanets.length === 0) return null;

        // Lagna is inside the planets array (name === 'Lagna')
        const lagnaData = rawPlanets.find((p: any) => p.name === 'Lagna') || null;

        // Hora current planet (for hora color detection)
        const horaCurrentPlanet = apiResponse?.detailed_panchanga?.hora_details?.current_planet || undefined;

        // Display modes
        const planetMode = DisplayModeService.getDisplayMode() as 'symbols' | 'short' | 'full';
        const rashiMode = (typeof window !== 'undefined' && (window as any).globalRashiDisplayMode) || 'full';

        // Build rashi labels (1-12 by containerIndex)
        const rashiLabels: string[] = [];
        for (let rashi = 1; rashi <= 12; rashi++) {
            // rashi number → containerIndex: 12 → 0, else same as rashi number
            const containerIndex = rashi === 12 ? 0 : rashi;
            rashiLabels.push(getRashiDisplayName(containerIndex, rashiMode));
        }
        const rashiFont = rashiMode === 'symbols' ? FONTS.chart.symbols : undefined;

        const charts: RashiChakraChart[] = [];

        for (const chartType of enabledCharts) {
            if (chartType === 'D1') {
                charts.push(this.buildD1Chart(rawPlanets, lagnaData, planetMode, rashiLabels, rashiFont, horaCurrentPlanet));
            } else if (chartType === 'D9') {
                charts.push(this.buildD9Chart(rawPlanets, lagnaData, planetMode, rashiLabels, rashiFont, horaCurrentPlanet));
            }
        }

        return { charts };
    }

    /** Build D1 Rasi chart data */
    private buildD1Chart(
        rawPlanets: any[],
        lagnaData: any,
        planetMode: 'symbols' | 'short' | 'full',
        rashiLabels: string[],
        rashiFont?: string,
        horaCurrentPlanet?: string
    ): RashiChakraChart {
        const planets: RashiChakraPlanet[] = [];
        const symFont = planetMode === 'symbols' ? FONTS.chart.symbols : undefined;

        // Lagna entry
        if (lagnaData) {
            planets.push({
                label: getPlanetDisplayName('Lg', planetMode),
                rashiNumber: lagnaData.rasi_number || 1,
                degree: lagnaData.longitude % 30,
                isRetrograde: false,
                isLagna: true,
                color: getElementColor(getElementType('Lg', horaCurrentPlanet)),
                font: symFont,
            });
        }

        for (const p of rawPlanets) {
            if (p.name === 'Lagna') continue;
            const isNode = p.short_name === 'Ra' || p.short_name === 'Ke';
            const elType = getElementType(p.short_name, horaCurrentPlanet, p.name);
            planets.push({
                label: getPlanetDisplayName(p.short_name, planetMode),
                rashiNumber: p.rasi_number,
                degree: p.longitude % 30,
                isRetrograde: isNode ? false : (p.is_retrograde || false),
                isLagna: false,
                color: getElementColor(elType),
                font: symFont,
            });
        }

        return {
            type: 'D1',
            title: 'Rasi',
            lagnaRashi: lagnaData?.rasi_number || 1,
            planets,
            rashiLabels,
            rashiFont,
        };
    }

    /** Build D9 Navamsa chart data */
    private buildD9Chart(
        rawPlanets: any[],
        lagnaData: any,
        planetMode: 'symbols' | 'short' | 'full',
        rashiLabels: string[],
        rashiFont?: string,
        horaCurrentPlanet?: string
    ): RashiChakraChart {
        const planets: RashiChakraPlanet[] = [];
        const symFont = planetMode === 'symbols' ? FONTS.chart.symbols : undefined;

        // Lagna navamsa
        if (lagnaData?.navamsa_rasi) {
            const lagnaNavamsaRashi = getHouseNumberFromRasiString(lagnaData.navamsa_rasi);
            planets.push({
                label: getPlanetDisplayName('Lg', planetMode),
                rashiNumber: lagnaNavamsaRashi,
                degree: (lagnaData.longitude * 9) % 30,
                isRetrograde: false,
                isLagna: true,
                color: getElementColor(getElementType('Lg', horaCurrentPlanet)),
                font: symFont,
            });
        }

        for (const p of rawPlanets) {
            if (p.name === 'Lagna') continue;
            if (!p.navamsa_rasi) continue;

            const isNode = p.short_name === 'Ra' || p.short_name === 'Ke';
            const elType = getElementType(p.short_name, horaCurrentPlanet, p.name);
            planets.push({
                label: getPlanetDisplayName(p.short_name, planetMode),
                rashiNumber: getHouseNumberFromRasiString(p.navamsa_rasi),
                degree: (p.longitude * 9) % 30,
                isRetrograde: isNode ? false : (p.is_retrograde || false),
                isLagna: false,
                color: getElementColor(elType),
                font: symFont,
            });
        }

        const lagnaNavamsaRashi = lagnaData?.navamsa_rasi
            ? getHouseNumberFromRasiString(lagnaData.navamsa_rasi)
            : 1;

        return {
            type: 'D9',
            title: 'Navamsa',
            lagnaRashi: lagnaNavamsaRashi,
            planets,
            rashiLabels,
            rashiFont,
        };
    }
}

// Global instance
export const chakraDataService = ChakraDataService.getInstance();